// Companion/blocks/ChatActionCard.tsx
// Adapted from beautifului.dev (MIT, © 2026 Shane Levine).
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Kbd } from '@/components/ui/kbd';
import { Spinner } from '@/components/ui/spinner';
import {
  actionProposalConfirmed,
  actionProposalDismissed,
  actionProposalUndoUnavailable,
  actionProposalUndone,
} from '@/slices/chat/chatSlice';
import type { ChatActionProposal } from '@/slices/chat/chatSlice';
import { enterStandalone } from '@/slices/studyContext/studyContextSlice';
import {
  confirmChatAction,
  dismissChatAction,
  undoChatAction,
} from '@/services/chatActions';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { showNewIntercomMessage } from '@/utils/intercom';
import { resolveChatBlockTarget } from './chatBlockTargets';
import {
  ChatActionCardDetails,
  ChatActionCardHeader,
  describeActionCard,
} from './ChatActionCardBody';

/** What "Open" means for a finished action, for screen readers. */
const OPEN_LABELS: Record<string, string> = {
  create_qbank_block: 'Open the new QBank block',
  contact_support: 'Open support chat',
};

/** When nothing — neither a known code nor the server — says anything better. */
const FAILURE_FALLBACK = 'That could not be done. Please try again.';

/**
 * Codes that mean the proposal is over rather than that it failed. The card goes
 * rather than showing a button the server will refuse again (ORA-750 review).
 */
const CLOSED_PROPOSAL_CODES = new Set([
  'proposal_dismissed',
  'proposal_expired',
  'proposal_not_found',
]);

/** Refusals that cannot become successful by pressing the same Undo again. */
const TERMINAL_UNDO_CODES = new Set([
  'conflict',
  'invalid_args',
  'not_found',
  'proposal_not_found',
  'undo_expired',
  'undo_unavailable',
]);

const UNDO_FAILURE_FALLBACK = 'That could not be undone. Please try again.';

export interface ChatActionCardProps {
  proposal: ChatActionProposal;
  conversationId: string;
  /** The drawer closes when the student opens what the action created. */
  onNavigate?: () => void;
}

/**
 * The confirmation card for a proposed action.
 *
 * Nothing an action tool proposes runs until the student presses Continue; the
 * card is the whole consent mechanism, so it shows what would happen (the
 * server-written summary) and the details it would run with. Confirming posts
 * the action id — and only the id — to the confirm endpoint, which validates
 * and executes outside the model loop.
 *
 * Whether the action already ran is **slice state, not component state**: this
 * card unmounts whenever the student opens what it created (the drawer closes),
 * and a remounted card must not offer to build a second QBank block. Local
 * state only covers the request that is in flight right now. The backend makes
 * confirm single-use as well; this is the near half of that pair.
 *
 * One proposal exists at a time, and it outlives the turn that proposed it
 * (ORA-750): a student answering "yes" must still find the button they were
 * answering. Closing it is the server's call, reported on `action.resolved` —
 * which can arrive already confirmed, because an affirmation auto-confirms and
 * runs the action server-side. So this card can appear in its done state
 * without anybody here having tapped it.
 */
const ChatActionCard = ({
  proposal,
  conversationId,
  onNavigate,
}: ChatActionCardProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [undoFailure, setUndoFailure] = useState<string | null>(null);
  const [, setExpiryRevision] = useState(0);
  // True once the support composer could not be opened — on a native shell
  // without the widget, or a blocked script. The message is not lost then: it
  // is shown for the student to copy into Help → Contact support.
  const [didSupportFail, setDidSupportFail] = useState(false);
  const isConfirmed = proposal.status === 'confirmed';
  const isUndone = proposal.status === 'undone';
  const isSettled = isConfirmed || isUndone;

  const undoExpiresAt = proposal.undo
    ? Date.parse(proposal.undo.expiresAt)
    : Number.NaN;

  const canUndo =
    isConfirmed &&
    proposal.undo?.available === true &&
    Number.isFinite(undoExpiresAt) &&
    Date.now() < undoExpiresAt;

  useEffect(() => {
    if (
      !isConfirmed ||
      proposal.undo?.available !== true ||
      !Number.isFinite(undoExpiresAt)
    ) {
      return;
    }

    const remaining = undoExpiresAt - Date.now();
    if (remaining <= 0) {
      setExpiryRevision((revision) => revision + 1);
      return;
    }

    const timer = setTimeout(
      () => setExpiryRevision((revision) => revision + 1),
      remaining
    );

    return () => clearTimeout(timer);
  }, [isConfirmed, proposal.undo?.available, undoExpiresAt]);

  /**
   * Close the card locally. Used for a card that is already answered — a receipt
   * the student is putting away — and as the local half of declining one.
   */
  const close = () =>
    dispatch(
      actionProposalDismissed({ conversationId, actionId: proposal.actionId })
    );

  /**
   * Decline a pending card, which has to reach the server (ORA-750 review,
   * blocker 1).
   *
   * The runtime reads the next message for an affirmation and auto-confirms
   * whatever is still PROPOSED, so a locally-only dismissed support card plus a
   * later "ok" would have sent the message the student had just refused. The
   * card still goes at once — their answer is not a request to wait — and a
   * request that does not land is logged and left to the server's own expiry.
   */
  const dismiss = () => {
    close();

    void dismissChatAction(proposal.actionId).then((result) => {
      if (result.ok) return;

      /**
       * The action had already run — a tap that raced the runtime reading the
       * student's own "yes". They asked for it gone, but it happened, and a
       * support message that is already with a human is something they have to
       * be told about: the card comes back as the receipt it should have been.
       */
      if (result.code === 'proposal_already_confirmed') {
        dispatch(
          actionProposalConfirmed({
            conversationId,
            actionId: proposal.actionId,
            outcome: { summary: null, target: null, result: null },
            reinstate: { ...proposal, status: 'confirmed', failure: undefined },
          })
        );

        return;
      }

      // Matches how the rest of the companion reports a background request it
      // could not complete (`useChatConversations`): logged, never surfaced.
      console.error(
        'Unable to dismiss a chat action proposal',
        proposal.name,
        result.code
      );
    });
  };

  /**
   * A confirm the SERVER ran and reported failed (ORA-743/ORA-750). The student
   * said "yes" in words rather than tapping, so there was no click to hang a
   * message on — and the card may have unmounted and come back since. It lands
   * in the same state a failed tap does: this message, and a button that now
   * reads "Try Again". `proposal.failure` is present-but-null when the server
   * offered no sentence of its own, which is the card's own fallback's job.
   *
   * Retrying clears it locally (`confirm` starts with `setFailure(null)`), and
   * the effect does not fire again for a value that has not changed.
   */
  useEffect(() => {
    if (proposal.failure === undefined) return;

    setFailure(proposal.failure ?? FAILURE_FALLBACK);
  }, [proposal.failure]);

  useEffect(() => {
    if (proposal.undoFailure === undefined) return;

    setUndoFailure(proposal.undoFailure ?? UNDO_FAILURE_FALLBACK);
  }, [proposal.undoFailure]);

  /**
   * Hand the server-composed message to Intercom's composer. Nothing sends:
   * the widget opens with the text in it, and the student presses send.
   */
  const openSupport = async (prefill: string) => {
    try {
      setDidSupportFail(!(await showNewIntercomMessage(prefill)));
    } catch {
      setDidSupportFail(true);
    }
  };

  const confirm = async () => {
    // Not while a request is in flight, and never once it succeeded: one
    // confirmation runs one action. A previous failure may be retried.
    if (isConfirming || isConfirmed) return;

    setIsConfirming(true);
    setFailure(null);
    const result = await confirmChatAction(proposal.actionId);
    setIsConfirming(false);

    if (result.ok) {
      dispatch(
        actionProposalConfirmed({
          conversationId,
          actionId: proposal.actionId,
          outcome: {
            summary: result.summary,
            target: result.target,
            result: result.result,
          },
        })
      );

      if (result.result?.kind === 'open_support') {
        await openSupport(result.result.prefill);
      }

      return;
    }

    /**
     * Some refusals are not failures at all, they are news about the card.
     *
     * `already_executed` means the action ran — the student tapped twice, or the
     * runtime confirmed it from their words while this request was in flight —
     * so the card owes them the done state, not a retry. A proposal the server
     * has closed (dismissed, expired, gone) has nothing left to offer, and a
     * card with a dead button is worse than no card.
     */
    if (result.code === 'already_executed') {
      dispatch(
        actionProposalConfirmed({
          conversationId,
          actionId: proposal.actionId,
          outcome: { summary: result.message, target: null, result: null },
        })
      );

      return;
    }

    if (CLOSED_PROPOSAL_CODES.has(result.code)) {
      close();
      return;
    }

    setFailure(result.message ?? FAILURE_FALLBACK);
  };

  const undo = async () => {
    if (!canUndo || isUndoing) return;

    setIsUndoing(true);
    setUndoFailure(null);
    const result = await undoChatAction(proposal.actionId);
    setIsUndoing(false);

    if (result.ok) {
      const resultUndo =
        result.result?.kind === 'plan_change' ||
        result.result?.kind === 'study_change'
          ? result.result.undo
          : undefined;

      dispatch(
        actionProposalUndone({
          conversationId,
          actionId: proposal.actionId,
          ...(resultUndo?.undoneAt ? { undoneAt: resultUndo.undoneAt } : {}),
        })
      );

      return;
    }

    const message = result.message ?? UNDO_FAILURE_FALLBACK;
    setUndoFailure(message);
    if (TERMINAL_UNDO_CODES.has(result.code)) {
      dispatch(
        actionProposalUndoUnavailable({
          conversationId,
          actionId: proposal.actionId,
          message,
        })
      );
    }
  };

  /**
   * Whether a failed attempt may be tried again from this card.
   *
   * Confirming `contact_support` sends a message to a human, and the server's
   * own failure copy says "ask me again and I'll re-draft it" rather than "tap
   * again" — because the proposal is spent by then, and because a failure it
   * could not be sure about may have landed. Offering a retry there invites a
   * duplicate refund request on a real support desk. Every other action is
   * idempotent enough to press twice, and keeps the button it always had.
   */
  const isRetryable = proposal.name !== 'contact_support';

  /**
   * The card is focusable so the keyboard can answer it without tabbing to a
   * button: Enter confirms, Escape skips. Only a keypress on the card itself
   * counts — a key pressed on a button inside it is that button's business.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (isSettled || isConfirming) return;
    // A spent proposal has no confirm button; Enter must not reach past that.
    if (failure && !isRetryable) {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
      }
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      void confirm();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss();
    }
  };

  const outcomeResult = isSettled ? (proposal.outcome?.result ?? null) : null;

  const supportPrefill =
    outcomeResult?.kind === 'open_support' ? outcomeResult.prefill : null;

  /**
   * The message is already with support (ORA-743). Nothing to open and nothing
   * to copy: the card says where the reply will arrive and stops. Deliberately
   * not the conversation id or the operator URL the result also carries — those
   * are for the audit row, not for the student.
   */
  const isSupportSent = outcomeResult?.kind === 'support_sent';

  const resolvedTarget = isConfirmed
    ? resolveChatBlockTarget(proposal.outcome?.target ?? null)
    : null;

  const { outsideResult, detailRows, confirmLabel } =
    describeActionCard(proposal);

  const showDetails =
    !isSettled && (outsideResult !== null || detailRows.length > 0);

  return (
    <div className="px-3 mobile:px-4">
      <div className="mx-auto w-full max-w-3xl">
        <section
          className="chat-motion mb-2 animate-chat-pop-in rounded-2xl border border-border-subtle bg-surface-raised p-4 shadow-low backdrop-blur-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          aria-label="Action confirmation"
          data-testid="chat-action-card"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <ChatActionCardHeader
            proposal={proposal}
            outsideResult={outsideResult}
            isSettled={isSettled}
          />

          <ChatActionCardDetails
            proposal={proposal}
            outsideResult={outsideResult}
            detailRows={detailRows}
            isOpen={showDetails}
          />

          {isSettled ? (
            <>
              <div className="mt-4 flex animate-chat-fade-in flex-wrap items-center gap-3">
                <span
                  aria-hidden="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-status-success"
                >
                  <CheckCircle size={18} weight="fill" />
                </span>
                <p
                  role="status"
                  className="m-0 min-w-0 flex-1 text-sm text-fg-primary"
                >
                  {isUndone ? 'Undone' : (proposal.outcome?.summary ?? 'Done.')}
                </p>
                {resolvedTarget ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-11 rounded-full normal-case mobile:h-8"
                    aria-label={
                      OPEN_LABELS[proposal.name] ?? 'Open what Ora created'
                    }
                    onClick={() => {
                      // A created block opens on its own, not as the Today
                      // session: standalone study loads it by id and sends the
                      // student back to the QBank page when they pause or
                      // finish (ORA-674).
                      if (resolvedTarget.standaloneBlockId) {
                        dispatch(
                          enterStandalone({
                            blockId: resolvedTarget.standaloneBlockId,
                            returnTo: 'qbank-start',
                          })
                        );
                      }

                      navigate(resolvedTarget.to);

                      onNavigate?.();
                    }}
                  >
                    Open
                  </Button>
                ) : null}
                {canUndo ? (
                  <Button
                    type="button"
                    size="pill"
                    variant="outline"
                    onClick={() => void undo()}
                    disabled={isUndoing}
                  >
                    {isUndoing ? (
                      <>
                        <Spinner size={14} aria-hidden="true" />
                        Undoing…
                      </>
                    ) : (
                      'Undo'
                    )}
                  </Button>
                ) : null}
                {/* Plain `ghost`, so the card's receipt adds no new
                    design-system exceptions of its own. */}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-11 mobile:h-8"
                  aria-label="Dismiss this card"
                  onClick={close}
                >
                  Done
                </Button>
                {supportPrefill ? (
                  // The composer opens on confirm; this reopens it, which is
                  // what a card that unmounted and came back can still offer.
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-11 rounded-full normal-case mobile:h-8"
                    onClick={() => void openSupport(supportPrefill)}
                  >
                    Open Support Chat
                  </Button>
                ) : null}
              </div>

              {canUndo &&
              proposal.undo?.approximate === true &&
              proposal.undo.note ? (
                <p className="m-0 mt-2 text-xs text-fg-secondary">
                  {proposal.undo.note}
                </p>
              ) : null}

              {undoFailure ? (
                <p role="alert" className="m-0 mt-2 text-xs text-fg-secondary">
                  {undoFailure}
                </p>
              ) : null}

              {isSupportSent ? (
                <p className="m-0 mt-3 text-xs text-fg-secondary">
                  A support conversation is open on your account — replies
                  arrive in the messenger and by email.
                </p>
              ) : null}

              {supportPrefill && didSupportFail ? (
                <div className="mt-3">
                  <p role="alert" className="m-0 text-xs text-fg-secondary">
                    Open Help → Contact support and paste this:
                  </p>
                  <pre
                    data-testid="chat-action-support-prefill"
                    className="m-0 mt-1 whitespace-pre-wrap break-words rounded-lg bg-surface-muted p-2 font-sans text-xs text-fg-primary"
                  >
                    {supportPrefill}
                  </pre>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-2 h-11 rounded-full normal-case mobile:h-8"
                    onClick={() => copyToClipboard(supportPrefill)}
                  >
                    Copy Message
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <>
              {failure ? (
                <div className="mt-3 flex animate-chat-fade-in items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-fg-secondary"
                  >
                    <WarningCircle size={18} />
                  </span>
                  <p
                    role="alert"
                    className="m-0 min-w-0 flex-1 text-sm text-fg-secondary"
                  >
                    {failure}
                  </p>
                </div>
              ) : null}

              {/* A spent proposal keeps only the way out: its failure message
                  has already told the student to ask Ora again, and the buttons
                  would offer a send that cannot happen twice. */}
              <div className="mt-3 flex flex-col-reverse gap-2 mobile:flex-row mobile:items-center mobile:justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-11 mobile:h-8"
                  onClick={dismiss}
                  disabled={isConfirming}
                >
                  {failure && !isRetryable ? 'Dismiss' : 'Skip'}
                </Button>
                {failure && !isRetryable ? null : (
                  <Button
                    type="button"
                    size="sm"
                    className="h-11 w-full mobile:h-8 mobile:w-auto"
                    onClick={() => void confirm()}
                    disabled={isConfirming}
                  >
                    {isConfirming ? (
                      <>
                        <Spinner size={14} aria-hidden="true" />
                        Working…
                      </>
                    ) : (
                      <>
                        {failure ? 'Try Again' : confirmLabel}
                        <Kbd
                          aria-hidden="true"
                          className="ml-1 hidden border-none bg-transparent text-current opacity-70 mobile:inline-flex"
                        >
                          ⏎
                        </Kbd>
                      </>
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ChatActionCard;
